import React, { useState, useRef, useCallback } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronUpIcon } from '../../icons';

interface PullToRefreshProps {
  /** 새로고침 핸들러 (Promise 반환) */
  onRefresh: () => Promise<void>;
  /** 자식 요소 */
  children: React.ReactNode;
  /** 당기기 임계값 (px) */
  threshold?: number;
  /** 비활성화 */
  disabled?: boolean;
  /** 새로고침 중 텍스트 */
  refreshingText?: string;
  /** 당기기 중 텍스트 */
  pullingText?: string;
  /** 릴리스 텍스트 */
  releaseText?: string;
}

type RefreshState = 'idle' | 'pulling' | 'ready' | 'refreshing';

export function PullToRefresh({
  onRefresh,
  children,
  threshold = 80,
  disabled = false,
  refreshingText = '새로고침 중...',
  pullingText = '당겨서 새로고침',
  releaseText = '놓아서 새로고침',
}: PullToRefreshProps) {
  const [state, setState] = useState<RefreshState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const isAtTopRef = useRef(true);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled || state === 'refreshing') return;

    // 스크롤이 맨 위인지 확인 (window 스크롤 기준 - sticky 호환)
    const scrollTop = window.scrollY ?? 0;
    if (scrollTop <= 0) {
      isAtTopRef.current = true;
      startYRef.current = e.touches[0].clientY;
    } else {
      isAtTopRef.current = false;
    }
  }, [disabled, state]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (disabled || state === 'refreshing' || !isAtTopRef.current) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;

    // 아래로 당기기만 허용
    if (diff <= 0) {
      setPullDistance(0);
      setState('idle');
      return;
    }

    // 저항 효과 적용 (당길수록 느려짐)
    const resistance = 0.5;
    const distance = diff * resistance;

    setPullDistance(distance);
    setState(distance >= threshold ? 'ready' : 'pulling');

    // 기본 스크롤 방지
    if (diff > 0) {
      e.preventDefault();
    }
  }, [disabled, state, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (disabled || state === 'refreshing') return;

    if (state === 'ready') {
      // 새로고침 실행
      setState('refreshing');
      setPullDistance(threshold);

      try {
        await onRefresh();
      } finally {
        setState('idle');
        setPullDistance(0);
      }
    } else {
      // 원위치
      setState('idle');
      setPullDistance(0);
    }
  }, [disabled, state, threshold, onRefresh]);

  const indicatorText = {
    idle: '',
    pulling: pullingText,
    ready: releaseText,
    refreshing: refreshingText,
  }[state];

  return (
    <div
      ref={containerRef}
      className="relative"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* 새로고침 인디케이터 */}
      <div
        className={`
          absolute left-0 right-0 flex items-center justify-center
          transition-all duration-200
          ${state === 'refreshing' ? 'bg-brand-50' : 'bg-surface-soft'}
        `}
        style={{
          height: pullDistance,
          top: 0,
          transform: 'translateY(-100%)',
        }}
      >
        {pullDistance > 0 && (
          <div className="flex items-center gap-2 text-sm text-ink-600">
            {state === 'refreshing' ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : state === 'ready' ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
            <span>{indicatorText}</span>
          </div>
        )}
      </div>

      {/* 콘텐츠 - pullDistance가 0일 때 transform 제거 (position: sticky 호환) */}
      <div
        style={pullDistance > 0 ? {
          transform: `translateY(${pullDistance}px)`,
          transition: state === 'refreshing' ? 'none' : 'transform 0.2s ease-out',
        } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

export default PullToRefresh;
